import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Palette, Layout, Code, Globe } from 'lucide-react';
const WireframeGlobe = () => <div className="relative w-64 h-64 md:w-80 md:h-80 flex items-center justify-center" data-id="element-170">
    {/* Core glow */}
    <div className="absolute w-3/4 h-3/4 bg-gold/20 rounded-full blur-3xl" data-id="element-171" />

    <motion.div animate={{
    rotate: 360
  }} transition={{
    duration: 40,
    repeat: Infinity,
    ease: 'linear'
  }} className="relative w-full h-full rounded-full border-2 border-gold/30 flex items-center justify-center shadow-[inset_0_0_50px_rgba(4,107,210,0.2)]" data-id="element-172">
      {/* Latitudes */}
      <div className="absolute w-full h-[30%] border border-gold/20 rounded-[50%]" data-id="element-173" />
      <div className="absolute w-full h-[70%] border border-gold/20 rounded-[50%]" data-id="element-174" />

      {/* Longitudes */}
      <div className="absolute w-[30%] h-full border border-gold/20 rounded-[50%]" data-id="element-175" />
      <div className="absolute w-[70%] h-full border border-gold/20 rounded-[50%]" data-id="element-176" />

      {/* Extra details */}
      <div className="absolute w-[15%] h-full border border-gold/10 rounded-[50%]" data-id="element-177" />
      <div className="absolute w-[85%] h-full border border-gold/10 rounded-[50%]" data-id="element-178" />
    </motion.div>
  </div>;
function OrbitCard({
  service,
  index,
  total,
  rotation
}: any) {
  const baseAngle = index / total * 360;
  const x = useTransform(rotation, (r: number) => {
    const angle = (baseAngle + r) * (Math.PI / 180);
    return Math.sin(angle) * 380;
  });
  const y = useTransform(rotation, (r: number) => {
    const angle = (baseAngle + r) * (Math.PI / 180);
    return Math.cos(angle) * 120;
  });
  const z = useTransform(rotation, (r: number) => {
    const angle = (baseAngle + r) * (Math.PI / 180);
    return Math.cos(angle);
  });
  const scale = useTransform(z, [-1, 1], [0.65, 1.1]);
  const opacity = useTransform(z, [-1, 1], [0.3, 1]);
  const zIndex = useTransform(z, (val: number) => Math.round(val * 50) + 50);
  return <motion.div style={{
    x,
    y,
    scale,
    opacity,
    zIndex,
    position: 'absolute'
  }} className="w-[280px]" data-id="element-179">
      <motion.div animate={{
      y: [-5, 5, -5]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: index * 0.5
    }} className="bg-dark-lighter/80 backdrop-blur-md p-6 rounded-2xl border border-white/10 hover:border-gold/50 transition-colors shadow-xl" data-id="element-180">
        <div className="w-14 h-14 bg-dark rounded-full flex items-center justify-center mb-5 text-gold border border-white/5" data-id="element-181">
          {service.icon}
        </div>
        <h3 className="text-xl font-bold text-white font-syne mb-3" data-id="element-182">
          {service.title}
        </h3>
        <p className="text-white/60 font-inter text-sm leading-relaxed" data-id="element-183">
          {service.description}
        </p>
      </motion.div>
    </motion.div>;
}
function MobileCard({
  service,
  index
}: any) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: index * 0.1
  }} className="bg-dark-lighter p-8 rounded-2xl border border-white/5" data-id="element-184">
      <div className="w-16 h-16 bg-dark rounded-full flex items-center justify-center mb-6 text-gold border border-white/5" data-id="element-185">
        {service.icon}
      </div>
      <h3 className="text-xl font-bold text-white font-syne mb-4" data-id="element-186">
        {service.title}
      </h3>
      <p className="text-white/60 font-inter text-sm leading-relaxed" data-id="element-187">
        {service.description}
      </p>
    </motion.div>;
}
export function ServicesSection() {
  const services = [{
    icon: <Palette className="w-8 h-8" data-id="element-188" />,
    title: 'UI/UX Design',
    description: 'Creating intuitive and visually appealing user interfaces that enhance user experience and engagement.'
  }, {
    icon: <Layout className="w-8 h-8" data-id="element-189" />,
    title: 'WordPress Dev',
    description: 'Custom WordPress themes and plugins tailored to your specific business needs and requirements.'
  }, {
    icon: <Code className="w-8 h-8" data-id="element-190" />,
    title: 'Web Development',
    description: 'Building responsive, fast, and secure websites using modern technologies and best practices.'
  }, {
    icon: <Globe className="w-8 h-8" data-id="element-191" />,
    title: 'Brand Identity',
    description: 'Crafting unique brand identities including logos, color palettes, and typography that stand out.'
  }];
  const containerRef = useRef(null);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end']
  });
  // Map scroll progress to 1 full rotation (360 degrees)
  const rotation = useTransform(scrollYProgress, [0, 1], [0, 360]);
  return <section id="services" ref={containerRef} className="bg-dark relative md:h-[250vh]" data-id="element-192">
      <div className="md:sticky md:top-0 md:h-screen w-full flex flex-col items-center md:justify-center overflow-hidden py-24 md:py-0" data-id="element-193">
        {/* Header */}
        <div className="md:absolute md:top-24 left-0 w-full text-center z-50 px-4 mb-16 md:mb-0 pointer-events-none" data-id="element-194">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-4xl md:text-5xl font-bold text-white font-syne mb-4" data-id="element-195">
            My <span className="text-gold" data-id="element-196">Services</span>
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-white/60 font-inter max-w-2xl mx-auto" data-id="element-197">
            I offer a comprehensive range of digital services to help your
            business grow and succeed in the digital age.
          </motion.p>
        </div>

        {/* Desktop 3D Orbit */}
        <div className="hidden md:flex relative w-full max-w-[1000px] h-[600px] items-center justify-center mt-20" data-id="element-198">
          <WireframeGlobe data-id="element-199" />
          {services.map((service, index) => <OrbitCard key={index} service={service} index={index} total={services.length} rotation={rotation} data-id="element-200" />)}
        </div>

        {/* Mobile Stack */}
        <div className="md:hidden w-full max-w-md mx-auto px-4 space-y-6" data-id="element-201">
          <div className="flex justify-center mb-12 scale-90" data-id="element-202">
            <WireframeGlobe data-id="element-203" />
          </div>
          {services.map((service, index) => <MobileCard key={index} service={service} index={index} data-id="element-204" />)}
        </div>
      </div>
    </section>;
}